import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import { pathways } from "@/brand/content";
import { ChamberChrome } from "@/components/common/ChamberChrome";

/**
 * PathwaysSection — Deep Mineral Blue chamber (V. PATHWAYS).
 *
 * Authoring moves:
 *   - Chamber chrome rail at top
 *   - Each primary pathway row has a MASSIVE Cormorant brass numeral
 *     set in the gutter (col 1) running OUTSIDE the headline column —
 *     reads like a chapter mark in a bound report.
 *   - Primary rows are taller, denser, with brass-rule "best when" caps
 *   - Supporting index uses brass-numbered §c, §d, §e and a hairline
 *     running-head label "SUPPORTING / V—02"
 */
export function PathwaysSection() {
  const primaries = pathways.filter((p) => p.primary);
  const secondaries = pathways.filter((p) => !p.primary);

  return (
    <section
      id="how-we-help"
      data-testid="pathways"
      style={{
        background: C.surfacePrimary,
        color: C.textOnDark,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <ChamberChrome
        numeral="V."
        title="Pathways"
        category="How we help"
        meta="Two primary · Three supporting"
        tone="dark"
      />

      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-14 py-24 lg:py-32">
        {/* Section intro */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-7">
              <span
                aria-hidden="true"
                className="inline-block h-px w-10"
                style={{ background: C.accentMetal }}
              />
              <p
                className="font-inter text-[11px] uppercase font-semibold tabular-nums"
                style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
              >
                § 05
              </p>
            </div>
            <h2
              className="font-cormorant"
              style={{
                color: C.textOnDark,
                fontSize: "clamp(2.2rem, 4.2vw, 3.6rem)",
                fontWeight: 400,
                lineHeight: 1.02,
                letterSpacing: "-0.012em",
              }}
            >
              Two primary pathways.
              <br />
              Three supporting forms
              <br />
              of the work.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6">
            <p
              className="font-inter leading-[1.7] max-w-[44ch]"
              style={{ color: C.whiteOnDark, fontSize: "1rem" }}
            >
              Most engagements lead with advisory at the leadership level or
              supervisory readiness for the managers carrying weight.
              Coaching, facilitation, and speaking support that core work as
              the moment calls for it.
            </p>
          </div>
        </div>

        {/* Primary + Supporting columns */}
        <div className="grid lg:grid-cols-12 gap-10">
          {/* PRIMARY pathways — col 1-8 — massive display rows with gutter numerals */}
          <div className="lg:col-span-8">
            <div
              className="flex items-center gap-4 mb-6"
              style={{ borderBottom: `1px solid ${C.brassSoft}`, paddingBottom: 12 }}
            >
              <p
                className="font-inter text-[10px] uppercase font-semibold tabular-nums"
                style={{ color: C.accentMetal, letterSpacing: "0.32em" }}
              >
                V — 01 &nbsp;·&nbsp; Primary
              </p>
            </div>
            {primaries.map((p, i) => (
              <a
                key={p.label}
                href="#contact"
                data-testid={`pathway-primary-${i}`}
                className="group block py-14 lg:py-16 transition-colors"
                style={{
                  borderBottom: `1px solid ${C.brassSoft}`,
                  color: C.textOnDark,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(22,24,27,0.25)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <div className="grid grid-cols-12 gap-x-6">
                  {/* Gutter numeral — massive Cormorant brass */}
                  <div className="col-span-12 lg:col-span-2 mb-4 lg:mb-0">
                    <span
                      className="font-cormorant tabular-nums"
                      style={{
                        color: C.accentMetal,
                        fontSize: "clamp(3.2rem, 5vw, 4.4rem)",
                        fontWeight: 400,
                        lineHeight: 1,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="col-span-12 lg:col-span-9">
                    <div className="flex items-start justify-between gap-6 mb-5">
                      <span
                        className="font-inter text-[10.5px] uppercase font-semibold"
                        style={{
                          color: C.accentMetal,
                          letterSpacing: "0.28em",
                        }}
                      >
                        Primary pathway
                      </span>
                      <ArrowUpRight
                        size={26}
                        weight="thin"
                        style={{ color: C.accentMetal }}
                        className="shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                      />
                    </div>
                    <h3
                      className="font-cormorant"
                      style={{
                        color: C.textOnDark,
                        fontSize: "clamp(2rem, 3.4vw, 3rem)",
                        fontWeight: 400,
                        lineHeight: 1.04,
                        letterSpacing: "-0.012em",
                      }}
                    >
                      {p.label}
                    </h3>
                    <p
                      className="mt-6 font-inter leading-[1.65] max-w-[54ch]"
                      style={{ color: C.whiteOnDark, fontSize: "1.02rem" }}
                    >
                      {p.descriptor}
                    </p>
                    <div
                      className="mt-6 pt-5 max-w-[52ch] flex items-baseline gap-4"
                      style={{ borderTop: `1px solid ${C.brassSoft}` }}
                    >
                      <span
                        className="font-inter text-[9.5px] uppercase font-bold shrink-0"
                        style={{
                          color: C.accentMetal,
                          letterSpacing: "0.30em",
                        }}
                      >
                        Best when
                      </span>
                      <p
                        className="font-cormorant italic"
                        style={{
                          color: C.textOnDark,
                          fontSize: "1rem",
                          lineHeight: 1.45,
                        }}
                      >
                        {p.bestWhen}
                      </p>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* SUPPORTING — col 9-12 — compressed brass-ruled index */}
          <aside
            className="lg:col-span-4 lg:pl-8"
            data-testid="pathways-supporting"
          >
            <div
              className="flex items-center gap-4 mb-6"
              style={{ borderBottom: `1px solid ${C.brassSoft}`, paddingBottom: 12 }}
            >
              <p
                className="font-inter text-[10px] uppercase font-semibold tabular-nums"
                style={{ color: C.accentMetal, letterSpacing: "0.32em" }}
              >
                V — 02 &nbsp;·&nbsp; Supporting
              </p>
            </div>
            <ul>
              {secondaries.map((p, i) => (
                <li
                  key={p.label}
                  style={{ borderBottom: `1px solid ${C.brassSoft}` }}
                >
                  <a
                    href="#contact"
                    data-testid={`pathway-secondary-${i}`}
                    className="group block py-7 transition-colors"
                  >
                    <div className="flex items-baseline gap-3 mb-3">
                      <span
                        className="font-inter text-[9.5px] uppercase font-bold tabular-nums"
                        style={{
                          color: C.accentMetal,
                          letterSpacing: "0.28em",
                        }}
                      >
                        §{String.fromCharCode(99 + i)}
                      </span>
                      <span
                        aria-hidden="true"
                        className="flex-1 h-px"
                        style={{ background: C.brassSoft }}
                      />
                    </div>
                    <h4
                      className="font-inter text-[13px] uppercase font-semibold mb-2"
                      style={{ color: C.textOnDark, letterSpacing: "0.14em" }}
                    >
                      {p.label}
                    </h4>
                    <p
                      className="font-inter leading-[1.55]"
                      style={{
                        color: C.whiteOnDarkSoft,
                        fontSize: "0.85rem",
                      }}
                    >
                      {p.descriptor}
                    </p>
                    <span
                      className="mt-3 inline-flex items-center gap-1.5 font-inter text-[10px] uppercase font-semibold"
                      style={{
                        color: C.accentMetal,
                        letterSpacing: "0.24em",
                      }}
                    >
                      Inquire
                      <ArrowRight
                        size={11}
                        weight="bold"
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
    </section>
  );
}
